import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { ArrowLeft, Download, Loader2, Sparkles, Send, FileImage, Edit3, Save, Share2, X, FileDown, Undo2, Play, Table2, ChevronDown, ChevronUp, Trash2, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { withBackend } from '../utils/api';
import { supabaseHelpers } from '../lib/supabase';
import ShareContentModal from './ShareContentModal';
import { getDefaultModel } from '../utils/models';

const DashboardWorkspace = forwardRef(function DashboardWorkspace({ preloadedData, onBack, onFullscreen, userId, onComplete, onUpdate, existingProjectId, externalChatMode, onChatMessage, chatMessages, onUsageRefresh }, ref) {
  const [phase, setPhase] = useState(preloadedData?.savedHtml ? 'preview' : 'generating');
  const [htmlContent, setHtmlContent] = useState(preloadedData?.savedHtml || '');
  const [title, setTitle] = useState(preloadedData?.savedTitle || '');
  const [editPrompt, setEditPrompt] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [editMode, setEditMode] = useState('view'); // 'view' | 'edit'
  const [isSaving, setIsSaving] = useState(false);
  const [projectId, setProjectId] = useState(existingProjectId || null);
  const [chatHistory, setChatHistory] = useState([]);
  const [generationStatus, setGenerationStatus] = useState('');
  const [showChatPanel, setShowChatPanel] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPresentMode, setShowPresentMode] = useState(false);
  // Undo history
  const [htmlHistory, setHtmlHistory] = useState([]);
  // Data panel
  const [showDataPanel, setShowDataPanel] = useState(false);
  const [rawDatasets, setRawDatasets] = useState(null);

  const iframeRef = useRef(null);
  const editTextareaRef = useRef(null);
  const chatEndRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const hasAutoSavedRef = useRef(!!existingProjectId);
  const isSaveUpdateRef = useRef(!!preloadedData?.savedHtml);
  const thumbnailRef = useRef(null);

  const outline = preloadedData?.outline;
  const questionnaireData = preloadedData?.questionnaireData;
  const flowConfig = preloadedData?.flowConfig;

  // Auto-generate on mount — dashboards generate directly from prompt + data (no outline step)
  useEffect(() => {
    if (preloadedData?.savedHtml) return;
    setTitle(flowConfig?.prompt ? 'Dashboard' : 'Dashboard');
    generateDashboard();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hide nav in fullscreen
  useEffect(() => {
    onFullscreen?.(true);
    return () => onFullscreen?.(false);
  }, [onFullscreen]);

  // Ctrl+Z undo + Delete key support
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && editMode === 'edit') {
        const activeEl = document.activeElement;
        if (activeEl?.tagName === 'INPUT' || activeEl?.tagName === 'TEXTAREA') return;
        const doc = iframeRef.current?.contentDocument;
        if (!doc) return;
        const selected = doc.querySelector('[style*="outline: 2px solid"]');
        if (selected && selected !== doc.body && selected !== doc.documentElement) {
          e.preventDefault();
          pushToHistory(htmlContent);
          selected.remove();
          serializeAndUpdateHtml();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [htmlHistory, editMode, htmlContent]);

  const pushToHistory = useCallback((html) => {
    if (html) setHtmlHistory(prev => [...prev.slice(-20), html]);
  }, []);

  const handleUndo = useCallback(() => {
    if (htmlHistory.length === 0) { toast.info('Nothing to undo'); return; }
    const previous = htmlHistory[htmlHistory.length - 1];
    setHtmlHistory(prev => prev.slice(0, -1));
    setHtmlContent(previous);
    toast.success('Undone');
  }, [htmlHistory]);

  const generateDashboard = async () => {
    setPhase('generating');
    setGenerationStatus('Generating dashboard...');
    thumbnailRef.current = null;

    try {
      // If connected to a cloud sheet and no local Excel data, fetch sheet data first
      let excelData = flowConfig?.excelData || null;
      if (!excelData && flowConfig?.googleSheetUrl) {
        setGenerationStatus('Fetching Google Sheet data...');
        const sheetResp = await fetch(withBackend('/api/google-sheets/fetch'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: flowConfig.googleSheetUrl }),
        });
        const sheetResult = await sheetResp.json();
        if (sheetResult.excel_data) {
          excelData = sheetResult.excel_data;
          setRawDatasets(sheetResult.excel_data);
        } else {
          toast.error('Could not fetch Google Sheet: ' + (sheetResult.error || 'Unknown error'));
        }
        setGenerationStatus('Generating dashboard...');
      }
      // OneDrive sheet — re-fetch live data the same way
      if (!excelData && flowConfig?.driveSourceInfo?.provider === 'microsoft' && flowConfig?.driveSourceInfo?.item_id) {
        setGenerationStatus('Fetching OneDrive sheet data...');
        const driveResp = await fetch(withBackend('/api/drive/microsoft/sheet-data'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId, item_id: flowConfig.driveSourceInfo.item_id }),
        });
        const driveResult = await driveResp.json();
        if (driveResult.excel_data) {
          excelData = driveResult.excel_data;
          setRawDatasets(driveResult.excel_data);
        } else {
          toast.error('Could not fetch OneDrive sheet: ' + (driveResult.error || 'Unknown error'));
        }
        setGenerationStatus('Generating dashboard...');
      }

      // Store raw datasets for data panel
      if (excelData && !rawDatasets) {
        setRawDatasets(excelData);
      }

      const response = await fetch(withBackend('/api/generate-dashboard-html-stream'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: flowConfig?.prompt || 'Create a dashboard from this data',
          dashboard_theme: flowConfig?.dashboardTheme || 'dark-analytics',
          dashboard_layout: flowConfig?.dashboardLayout || 'balanced',
          chart_preference: flowConfig?.chartPreference || 'mixed',
          html_model: flowConfig?.htmlModel || getDefaultModel(),
          language: flowConfig?.language || 'en',
          excel_data: excelData,
          logo_url: flowConfig?.logoUrl || '',
          special_instructions: questionnaireData?.specialInstructions || '',
          google_sheet_url: flowConfig?.googleSheetUrl || '',
          refresh_interval: flowConfig?.refreshInterval || 60,
          backend_url: withBackend(''),
          brand_identity_id: flowConfig?.brandIdentityId || '',
          user_id: userId || '',
          design_reference_images: flowConfig?.designReferenceImages || [],
        }),
      });

      if (response.status === 403) {
        const err = await response.json().catch(() => ({}));
        if (err.error === 'usage_limit_exceeded') {
          toast.error(err.details?.reason === 'trial_expired'
            ? 'Your 7-day trial has ended. Upgrade to keep creating.'
            : 'You\'ve reached your monthly limit for dashboard generation. Upgrade for more.');
          setPhase('error');
          return;
        }
      }
      if (!response.ok) throw new Error('Failed to start generation');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            switch (event.type) {
              case 'chunk':
                setPhase('streaming');
                setHtmlContent(event.text);
                break;
              case 'complete':
                setHtmlContent(event.html);
                setPhase('preview');
                onUsageRefresh?.();
                onChatMessage?.('Dashboard generated! You can now edit it or ask questions about it.');
                break;
              case 'thumbnail':
                thumbnailRef.current = event.preview_url;
                break;
              case 'error':
                throw new Error(event.error);
              default:
                break;
            }
          } catch (parseErr) {
            if (parseErr.message && !parseErr.message.includes('Unexpected token'))
              throw parseErr;
          }
        }
      }
    } catch (error) {
      console.error('Dashboard generation error:', error);
      toast.error(error.message || 'Failed to generate dashboard');
      setPhase('error');
    }
  };

  // Write HTML to iframe
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !htmlContent) return;

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;

    // Inject CSS fix to ensure dashboard background extends full width (no off-color sides)
    let fixedHtml = htmlContent;
    const bgFixCSS = '<style>html, body { margin: 0; padding: 0; min-width: 100%; min-height: 100%; }</style>';
    if (fixedHtml.includes('</head>')) {
      fixedHtml = fixedHtml.replace('</head>', bgFixCSS + '</head>');
    } else if (fixedHtml.includes('<body')) {
      fixedHtml = fixedHtml.replace('<body', bgFixCSS + '<body');
    }

    doc.open();
    doc.write(fixedHtml);
    doc.close();

    // Safety net: sync body bg with the dashboard's main container bg
    setTimeout(() => {
      try {
        const body = doc.body;
        if (!body) return;
        const computed = doc.defaultView?.getComputedStyle(body);
        const bg = computed?.backgroundColor;
        // If body is transparent or white but dashboard content is dark, copy the container bg
        if (bg === 'rgba(0, 0, 0, 0)' || bg === 'transparent') {
          // Try to find the first major child div's background
          const mainContainer = body.querySelector('[class*="bg-"]') || body.firstElementChild;
          if (mainContainer) {
            const containerBg = doc.defaultView?.getComputedStyle(mainContainer)?.backgroundColor;
            if (containerBg && containerBg !== 'rgba(0, 0, 0, 0)' && containerBg !== 'transparent') {
              body.style.backgroundColor = containerBg;
              doc.documentElement.style.backgroundColor = containerBg;
            }
          }
          // Fallback: detect dark theme from HTML
          if (!body.style.backgroundColor) {
            const htmlStr = htmlContent.toLowerCase();
            const isDark = htmlStr.includes('bg-slate-900') || htmlStr.includes('bg-gray-900') ||
              htmlStr.includes('bg-neutral-900') || htmlStr.includes('#0f172a') ||
              htmlStr.includes('#1e293b') || htmlStr.includes('#111827') ||
              htmlStr.includes('dark-analytics') || htmlStr.includes('bg-zinc-900');
            if (isDark) {
              body.style.backgroundColor = '#0f172a';
              doc.documentElement.style.backgroundColor = '#0f172a';
            }
          }
        }
      } catch {}
    }, 500);

    const resizeIframe = () => {
      try {
        const body = doc.body;
        const html = doc.documentElement;
        if (body && html) {
          const height = Math.max(
            body.scrollHeight, body.offsetHeight,
            html.clientHeight, html.scrollHeight, html.offsetHeight
          );
          iframe.style.height = height + 'px';
        }
      } catch {}
    };

    setTimeout(resizeIframe, 500);
    setTimeout(resizeIframe, 1500);
    setTimeout(resizeIframe, 3000);

    // Re-apply edit mode after rewrite
    if (editMode === 'edit') {
      setTimeout(() => { enableTextEditing(); enableStylePicker(); }, 600);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [htmlContent, phase]);

  // ─── Editing functions ───

  // Auto-save debounce after inline edits
  const editSaveTimerRef = useRef(null);
  const scheduleEditAutoSave = useCallback(() => {
    if (editSaveTimerRef.current) clearTimeout(editSaveTimerRef.current);
    editSaveTimerRef.current = setTimeout(() => {
      const iframe = iframeRef.current;
      const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
      if (!doc) return;
      const clonedDoc = doc.cloneNode(true);
      clonedDoc.querySelectorAll('[contenteditable]').forEach(el => el.removeAttribute('contenteditable'));
      clonedDoc.querySelectorAll('script').forEach(s => { if (s.textContent?.includes('__stylePickerActive')) s.remove(); });
      clonedDoc.querySelectorAll('*').forEach(el => {
        if (el.style.outline) el.style.removeProperty('outline');
        if (el.style.outlineOffset) el.style.removeProperty('outline-offset');
        if (el.style.cursor === 'text') el.style.removeProperty('cursor');
        if (el.getAttribute('style') === '') el.removeAttribute('style');
      });
      const serialized = '<!DOCTYPE html>' + clonedDoc.documentElement.outerHTML;
      isSaveUpdateRef.current = true;
      setHtmlContent(serialized);
    }, 2000);
  }, []);

  const enableTextEditing = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return;

    const textElements = doc.querySelectorAll('h1, h2, h3, h4, h5, h6, p, span, li, td, th, a, label, figcaption');
    textElements.forEach(el => {
      el.contentEditable = 'true';
      el.style.outline = 'none';
      el.style.cursor = 'text';
      el.addEventListener('focus', () => {
        el.style.outline = '2px solid #3b82f6';
        el.style.outlineOffset = '2px';
        el.style.borderRadius = '2px';
      });
      el.addEventListener('blur', () => {
        el.style.outline = 'none';
        scheduleEditAutoSave();
      });
      el.addEventListener('input', () => {
        scheduleEditAutoSave();
      });
    });
  }, [scheduleEditAutoSave]);

  const disableTextEditing = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return;
    const textElements = doc.querySelectorAll('[contenteditable="true"]');
    textElements.forEach(el => {
      el.contentEditable = 'false';
      el.style.outline = 'none';
      el.style.cursor = '';
    });
  }, []);

  const enableStylePicker = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return;
    const script = doc.createElement('script');
    script.textContent = `
      (function() {
        if (window.__stylePickerActive) return;
        window.__stylePickerActive = true;
        var selected = null;
        document.addEventListener('click', function(e) {
          e.preventDefault(); e.stopPropagation();
          if (selected) selected.style.outline = '';
          selected = e.target;
          selected.style.outline = '2px solid #8b5cf6';
          selected.style.outlineOffset = '2px';
        }, true);
        document.addEventListener('mouseover', function(e) {
          if (e.target !== selected) e.target.style.outline = '1px dashed #8b5cf680';
        }, true);
        document.addEventListener('mouseout', function(e) {
          if (e.target !== selected) e.target.style.outline = '';
        }, true);
      })();
    `;
    doc.body.appendChild(script);
  }, []);

  const disableStylePicker = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return;
    doc.querySelectorAll('*').forEach(el => { el.style.outline = ''; });
    if (doc.defaultView) doc.defaultView.__stylePickerActive = false;
  }, []);

  // Single edit toggle — enables both text editing and style picking
  const handleEditToggle = () => {
    if (editMode === 'edit') {
      disableTextEditing();
      disableStylePicker();
      serializeAndUpdateHtml();
      setEditMode('view');
    } else {
      pushToHistory(htmlContent);
      setEditMode('edit');
      setTimeout(() => { enableTextEditing(); enableStylePicker(); }, 100);
    }
  };

  const cleanupEditArtifacts = useCallback((doc) => {
    if (!doc) return;
    // Remove contenteditable attributes
    doc.querySelectorAll('[contenteditable]').forEach(el => {
      el.removeAttribute('contenteditable');
    });
    // Remove style picker injected scripts
    doc.querySelectorAll('script').forEach(script => {
      if (script.textContent && script.textContent.includes('__stylePickerActive')) {
        script.remove();
      }
    });
    // Clean up inline outline/cursor styles added by edit mode
    doc.querySelectorAll('*').forEach(el => {
      if (el.style.outline) el.style.removeProperty('outline');
      if (el.style.outlineOffset) el.style.removeProperty('outline-offset');
      if (el.style.cursor === 'text') el.style.removeProperty('cursor');
      // Remove empty style attributes
      if (el.getAttribute('style') === '') el.removeAttribute('style');
    });
  }, []);

  const serializeAndUpdateHtml = () => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return;
    cleanupEditArtifacts(doc);
    const serialized = '<!DOCTYPE html>' + doc.documentElement.outerHTML;
    setHtmlContent(serialized);
  };

  const serializeHtml = () => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return htmlContent;
    cleanupEditArtifacts(doc);
    return '<!DOCTYPE html>' + doc.documentElement.outerHTML;
  };

  // ─── Refresh data from connected sheet ───

  const [isRefreshing, setIsRefreshing] = useState(false);

  const hasDataSource = !!(flowConfig?.googleSheetUrl || (flowConfig?.driveSourceInfo?.provider === 'microsoft' && flowConfig?.driveSourceInfo?.item_id));

  const handleRefreshData = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    toast.loading('Fetching latest data...', { id: 'data-refresh' });

    try {
      let freshExcelData = null;

      if (flowConfig?.googleSheetUrl) {
        const resp = await fetch(withBackend('/api/google-sheets/fetch'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: flowConfig.googleSheetUrl }),
        });
        const result = await resp.json();
        if (result.excel_data) freshExcelData = result.excel_data;
        else throw new Error(result.error || 'Failed to fetch Google Sheet');
      } else if (flowConfig?.driveSourceInfo?.provider === 'microsoft' && flowConfig?.driveSourceInfo?.item_id) {
        const resp = await fetch(withBackend('/api/drive/microsoft/sheet-data'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId, item_id: flowConfig.driveSourceInfo.item_id }),
        });
        const result = await resp.json();
        if (result.excel_data) freshExcelData = result.excel_data;
        else throw new Error(result.error || 'Failed to fetch OneDrive sheet');
      }

      if (!freshExcelData) throw new Error('No data source configured');

      setRawDatasets(freshExcelData);

      // Ask the AI to update the dashboard with new data
      toast.loading('Updating dashboard with fresh data...', { id: 'data-refresh' });
      pushToHistory(htmlContent);

      const dataPreview = freshExcelData.map(s => {
        const headerRow = s.headers.join(' | ');
        const sampleRows = (s.rows || []).slice(0, 5).map(r => r.join(' | ')).join('\n');
        return `Sheet: ${s.sheet_name}\n${headerRow}\n${sampleRows}\n(${s.total_rows} total rows)`;
      }).join('\n\n');

      const currentHtml = serializeHtml();
      const response = await fetch(withBackend('/edit-html-slide'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          request_id: crypto.randomUUID(),
          html_source: currentHtml,
          edit_prompt: `The connected spreadsheet data has been updated. Here is the latest data:\n\n${dataPreview}\n\nUpdate ALL numbers, charts, tables, and data visualizations in this dashboard to reflect the new data above. Keep the same layout and design — only change the data values, chart data, table rows, KPI numbers, and any calculated metrics.`,
          html_model: flowConfig?.htmlModel || getDefaultModel(),
          content_type: 'dashboard',
          brand_identity_id: flowConfig?.brandIdentityId || '',
          user_id: userId || '',
          run_qa: flowConfig?.runQa !== 'no',
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || errData.error || 'Failed to update dashboard');
      }
      const data = await response.json();
      if (data.html_source) {
        setHtmlContent(data.html_source);
        setChatHistory(prev => [...prev, { role: 'bot', content: 'Dashboard refreshed with latest data.', timestamp: Date.now() }]);
        onChatMessage?.('Dashboard refreshed with latest data.');
      }
      toast.success('Dashboard refreshed with latest data!', { id: 'data-refresh' });
    } catch (err) {
      console.error('Data refresh error:', err);
      toast.error(err.message || 'Failed to refresh data', { id: 'data-refresh' });
    } finally {
      setIsRefreshing(false);
    }
  };

  // ─── Expose methods for UnifiedWorkspace toolbar ───

  useImperativeHandle(ref, () => ({
    sendEdit: (message, options) => handleEditRequest(message, options),
    getChatHistory: () => chatHistory,
    getData: () => ({ html: htmlContent, title }),
    save: () => handleSave(),
    share: () => setShowShareModal(true),
    present: () => setShowPresentMode(true),
    exportPng: () => handleDownloadPNG(),
    exportPdf: () => handleDownloadPDF(),
    exportHtml: () => handleDownloadHTML(),
    edit: () => handleEditToggle(),
    undo: () => handleUndo(),
    refreshData: () => handleRefreshData(),
    hasDataSource,
    getExportState: () => ({ exporting: false, saving: isSaving || isRefreshing }),
    getEditMode: () => editMode,
  }));

  // ─── AI Edit ───

  const handleEditRequest = async (messageOverride, options = {}) => {
    const request = (messageOverride || editPrompt).trim();
    if (!request) return;
    setEditPrompt('');
    setIsRegenerating(true);
    pushToHistory(htmlContent);
    setChatHistory(prev => [...prev, { role: 'user', content: request, timestamp: Date.now() }]);

    try {
      const currentHtml = serializeHtml();
      const response = await fetch(withBackend('/edit-html-slide'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          request_id: crypto.randomUUID(),
          html_source: currentHtml,
          edit_prompt: request,
          html_model: flowConfig?.htmlModel || getDefaultModel(),
          reference_images: options.referenceImages || undefined,
          content_type: 'dashboard',
          brand_identity_id: flowConfig?.brandIdentityId || '',
          user_id: userId || '',
          run_qa: flowConfig?.runQa !== 'no',
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || errData.error || 'Failed to update dashboard');
      }
      const data = await response.json();
      if (data.html_source) {
        setHtmlContent(data.html_source);
        setChatHistory(prev => [...prev, { role: 'bot', content: 'Dashboard updated.', timestamp: Date.now() }]);
        onChatMessage?.('Dashboard updated.');
      }
      toast.success('Dashboard updated!');
    } catch (err) {
      toast.error(err.message || 'Failed to update dashboard');
      setChatHistory(prev => [...prev, { role: 'bot', content: 'Failed to update. Please try again.', timestamp: Date.now(), error: true }]);
      onChatMessage?.('Failed to update. Please try again.');
    } finally {
      setIsRegenerating(false);
    }
  };

  // ─── Save ───

  const handleSave = () => {
    if (!htmlContent) { toast.error('Nothing to save'); return; }
    setIsSaving(true);
    try {
      const serialized = serializeHtml();
      isSaveUpdateRef.current = true;
      setHtmlContent(serialized);
      if (editMode === 'edit') {
        disableTextEditing();
        disableStylePicker();
        setEditMode('view');
      }
      handleSaveProject();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveProject = async () => {
    if (!userId) return;
    const currentHtml = serializeHtml();
    if (!currentHtml) return;

    try {
      const dashboardData = {
        type: 'dashboard',
        html_content: currentHtml,
        title: title,
        panel_count: 1,
        metadata: {
          generationMode: flowConfig?.generationMode,
          dashboardTheme: flowConfig?.dashboardTheme,
          questionnaireData: questionnaireData,
          googleSheetUrl: flowConfig?.googleSheetUrl || null,
          refreshInterval: flowConfig?.refreshInterval || 60,
          updated_at: new Date().toISOString(),
          chatHistory: chatMessages || [],
        }
      };

      let thumb = thumbnailRef.current;
      if (!thumb && htmlContent) {
        try {
          const resp = await fetch(withBackend('/screenshot-html'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ html_source: htmlContent }),
          });
          if (resp.ok) {
            const result = await resp.json();
            thumb = result.preview_url;
            thumbnailRef.current = thumb;
          }
        } catch (e) { console.warn('Thumbnail generation failed:', e); }
      }

      if (projectId) {
        const { error } = await supabaseHelpers.updateProject(projectId, {
          name: title,
          analysis_data: dashboardData,
          status: 'completed',
          ...(thumb ? { thumbnail_url: thumb } : {}),
        });
        if (error) throw error;
        if (onUpdate) onUpdate(projectId, dashboardData);
        toast.success('Dashboard saved!');
      } else {
        const { data, error } = await supabaseHelpers.createProject({
          user_id: userId,
          name: title,
          client_name: 'Dashboard',
          status: 'completed',
          slide_count: 1,
          analysis_data: dashboardData,
          ...(thumb ? { thumbnail_url: thumb } : {}),
        });
        if (error) throw error;
        if (data) {
          setProjectId(data.id);
          hasAutoSavedRef.current = true;
          if (onComplete) onComplete({
            projectId: data.id,
            dashboardData,
            preview_url: thumbnailRef.current,
          });
          toast.success('Dashboard saved!');
        }
      }
    } catch (error) {
      console.error('Save project error:', error);
      toast.error('Failed to save project');
    }
  };

  // Autosave
  useEffect(() => {
    if (phase !== 'preview' || !userId) return;
    if (isSaveUpdateRef.current) { isSaveUpdateRef.current = false; return; }
    if (!htmlContent) return;

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      handleSaveProject();
    }, 5000);

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [htmlContent, phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Export ───

  const handleDownloadPNG = async () => {
    if (!htmlContent) return;
    toast.info('Generating PNG...');
    try {
      const response = await fetch(withBackend('/screenshot-html'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ html_source: htmlContent, width: 1400, full_page: true }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.screenshot) {
          const link = document.createElement('a');
          link.download = `${title || 'dashboard'}.png`;
          link.href = `data:image/png;base64,${data.screenshot}`;
          link.click();
          toast.success('PNG downloaded!');
        }
      } else {
        handleDownloadHTML();
      }
    } catch {
      handleDownloadHTML();
    }
  };

  const handleDownloadHTML = () => {
    if (!htmlContent) return;
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = `${title || 'dashboard'}.html`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('HTML downloaded!');
  };

  const handleDownloadPDF = async () => {
    if (!htmlContent) return;
    toast.loading('Generating PDF...', { id: 'pdf-export' });
    try {
      const response = await fetch(withBackend('/export-slides-as-pdf'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slides: [htmlContent], presentation_title: title || 'dashboard', width: 1400, height: 900, orientation: 'landscape' }),
      });
      if (!response.ok) throw new Error('PDF generation failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title || 'dashboard'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF downloaded!', { id: 'pdf-export' });
    } catch (err) {
      console.error('PDF export error:', err);
      handleDownloadHTML();
      toast.info('PDF export unavailable — HTML downloaded instead', { id: 'pdf-export' });
    }
  };

  return (
    <div className={`${externalChatMode ? 'h-full' : 'h-screen'} flex flex-col ${externalChatMode ? 'bg-slate-800' : 'bg-background'}`}>
      {/* Header — only shown when NOT in external chat mode (UnifiedWorkspace provides toolbar) */}
      {!externalChatMode && (
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-card">
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold text-foreground truncate">{title}</h2>
            <p className="text-xs text-muted-foreground">
              {phase === 'generating' || phase === 'streaming' ? 'Generating dashboard...' : 'Ready'}
            </p>
          </div>
          {phase === 'preview' && (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant={editMode === 'edit' ? 'default' : 'outline'}
                className={editMode === 'edit' ? 'bg-blue-500 hover:bg-blue-600 text-white' : ''}
                onClick={handleEditToggle}
              >
                <Edit3 className="w-3.5 h-3.5 mr-1.5" /> {editMode === 'edit' ? 'Done' : 'Edit'}
              </Button>
              {editMode === 'edit' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  onClick={() => {
                    const doc = iframeRef.current?.contentDocument;
                    if (!doc) return;
                    const selected = doc.querySelector('[style*="outline: 2px solid"]');
                    if (selected && selected !== doc.body && selected !== doc.documentElement) {
                      pushToHistory(htmlContent);
                      selected.remove();
                      serializeAndUpdateHtml();
                    }
                  }}
                  title="Delete selected element (Del)"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
              {htmlHistory.length > 0 && (
                <Button size="sm" variant="outline" onClick={handleUndo}>
                  <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Undo
                </Button>
              )}
              <div className="w-px h-6 bg-border/50 mx-1" />
              <Button variant="outline" size="sm" onClick={handleSave} disabled={isSaving}>
                <Save className={`w-3.5 h-3.5 mr-1.5 ${isSaving ? 'animate-spin' : ''}`} /> Save
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowShareModal(true)}>
                <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share
              </Button>
              {hasDataSource && (
                <Button variant="outline" size="sm" onClick={handleRefreshData} disabled={isRefreshing}>
                  <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isRefreshing ? 'animate-spin' : ''}`} /> Refresh
                </Button>
              )}
              {rawDatasets && (
                <Button variant="outline" size="sm" onClick={() => setShowDataPanel(!showDataPanel)}>
                  <Table2 className="w-3.5 h-3.5 mr-1.5" /> Data
                </Button>
              )}
              <Button size="sm" onClick={handleDownloadPNG} className="bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600">
                <FileImage className="w-3.5 h-3.5 mr-1.5" /> PNG
              </Button>
              <Button size="sm" onClick={handleDownloadPDF} className="bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600">
                <FileDown className="w-3.5 h-3.5 mr-1.5" /> PDF
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Edit mode indicator */}
      {editMode === 'edit' && (
        <div className="px-4 py-1.5 text-xs text-white text-center bg-blue-500">
          Click text to edit \u00b7 Click elements to select \u00b7 Del to delete \u00b7 Ctrl+Z to undo
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Dashboard Preview */}
        <div className={`flex-1 overflow-auto ${phase === 'preview' || phase === 'streaming' ? 'bg-slate-800' : 'bg-slate-100 dark:bg-slate-900'}`}>
          {phase === 'generating' && (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="relative">
                <Loader2 className="w-12 h-12 text-amber-500 animate-spin" />
                <Sparkles className="w-5 h-5 text-amber-400 absolute -top-1 -right-1 animate-pulse" />
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                {generationStatus || 'Generating dashboard...'}
              </p>
            </div>
          )}

          {phase === 'streaming' && htmlContent && (
            <div className="w-full py-2 relative">
              <div className="absolute top-8 left-1/2 -translate-x-1/2 z-10 bg-amber-500/90 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-2 shadow-lg backdrop-blur-sm">
                <Loader2 className="w-3 h-3 animate-spin" /> Generating...
              </div>
              <div className="w-full overflow-hidden">
                <iframe
                  ref={iframeRef}
                  title="Dashboard Preview"
                  style={{ width: '100%', minHeight: 900, border: 'none', display: 'block' }}
                  sandbox="allow-scripts allow-same-origin"
                />
              </div>
            </div>
          )}

          {phase === 'error' && (
            <div className="flex flex-col items-center justify-center h-full">
              <p className="text-sm text-red-500 mb-3">Failed to generate dashboard</p>
              <Button variant="outline" onClick={() => generateDashboard()}>
                Try Again
              </Button>
            </div>
          )}

          {phase === 'preview' && htmlContent && (
            <div className="w-full relative">
              <div className="w-full overflow-hidden relative">
                <iframe
                  ref={iframeRef}
                  title="Dashboard Preview"
                  style={{ width: '100%', height: '100%', minHeight: 900, border: 'none', display: 'block' }}
                  sandbox="allow-scripts allow-same-origin"
                />
              </div>
            </div>
          )}
        </div>

        {/* Data Panel — collapsible raw dataset view */}
        {showDataPanel && rawDatasets && (
          <div className="w-[400px] border-l border-border/50 flex flex-col bg-card overflow-hidden">
            <div className="px-4 py-3 border-b border-border/50 flex items-center justify-between">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Table2 className="w-4 h-4 text-amber-500" />
                Raw Dataset
              </h3>
              <button onClick={() => setShowDataPanel(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-auto">
              {rawDatasets.map((sheet, si) => (
                <div key={si} className="border-b border-border/30">
                  <div className="px-4 py-2 bg-muted/30 text-xs font-medium text-foreground">
                    {sheet.sheet_name || `Sheet ${si + 1}`} ({sheet.total_rows || sheet.rows?.length || 0} rows)
                  </div>
                  <div className="overflow-auto max-h-[400px]">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-card">
                        <tr>
                          {(sheet.headers || []).map((h, hi) => (
                            <th key={hi} className="px-2 py-1.5 text-left font-medium text-muted-foreground border-b border-border/30 whitespace-nowrap">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(sheet.rows || []).slice(0, 100).map((row, ri) => (
                          <tr key={ri} className="hover:bg-muted/20">
                            {row.map((cell, ci) => (
                              <td key={ci} className="px-2 py-1 border-b border-border/10 whitespace-nowrap max-w-[200px] truncate">
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Chat Toggle Button (hidden in external chat mode) */}
        {phase === 'preview' && !showChatPanel && !externalChatMode && (
          <button
            onClick={() => setShowChatPanel(true)}
            className="absolute top-4 right-4 z-20 bg-amber-500 hover:bg-amber-600 text-white rounded-full p-3 shadow-lg transition-all hover:scale-105"
            title="Edit with AI"
          >
            <Sparkles className="w-5 h-5" />
          </button>
        )}

        {/* Right Panel — Edit with AI (overlay, hidden in external chat mode) */}
        {phase === 'preview' && showChatPanel && !externalChatMode && (
          <div className="absolute top-0 right-0 bottom-0 w-[340px] z-20 border-l border-border/50 flex flex-col bg-card/95 backdrop-blur-md shadow-2xl">
            <div className="px-4 py-3 border-b border-border/50 flex items-center justify-between">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-500" />
                Edit with AI
              </h3>
              <button
                onClick={() => setShowChatPanel(false)}
                className="text-muted-foreground hover:text-foreground transition-colors text-lg leading-none px-1"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3">
              {chatHistory.length === 0 ? (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground mb-3">Quick suggestions:</p>
                  {[
                    'Switch to dark theme',
                    'Add more chart variety',
                    'Make KPI cards more prominent',
                    'Add a data table section',
                    'Use a different color palette',
                    'Make it more compact',
                  ].map((suggestion, i) => (
                    <button
                      key={i}
                      onClick={() => setEditPrompt(suggestion)}
                      className="block w-full text-left text-xs px-3 py-2 rounded-lg border border-border/50 hover:border-amber-400/50 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors text-muted-foreground"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {chatHistory.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] text-xs px-3 py-2 rounded-lg ${
                        msg.role === 'user'
                          ? 'bg-amber-500 text-white'
                          : msg.error
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                            : 'bg-muted text-foreground'
                      }`}>
                        {msg.role === 'bot' && <Sparkles className="w-3 h-3 inline mr-1 opacity-60" />}
                        {msg.content}
                      </div>
                    </div>
                  ))}
                  {isRegenerating && (
                    <div className="flex justify-start">
                      <div className="bg-muted text-foreground text-xs px-3 py-2 rounded-lg flex items-center gap-2">
                        <Loader2 className="w-3 h-3 animate-spin" /> Updating...
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
              )}
            </div>
            <div className="px-4 py-3 border-t border-border/50">
              <p className="text-xs text-muted-foreground mb-2">Describe what to change</p>
              <div className="flex gap-2">
                <textarea
                  ref={editTextareaRef}
                  value={editPrompt}
                  onChange={(e) => setEditPrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleEditRequest();
                    }
                  }}
                  placeholder="e.g., Add a pie chart for market share..."
                  rows={1}
                  className="flex-1 text-sm bg-transparent border border-border/50 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-amber-400/50"
                />
                <button
                  onClick={handleEditRequest}
                  disabled={!editPrompt.trim() || isRegenerating}
                  className="w-9 h-9 rounded-lg bg-amber-500 text-white flex items-center justify-center hover:bg-amber-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Present mode — fullscreen overlay */}
      {showPresentMode && htmlContent && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col">
          <div className="flex items-center justify-between px-4 py-2 bg-black/80">
            <span className="text-white text-sm font-medium">{title}</span>
            <button onClick={() => setShowPresentMode(false)} className="text-white/70 hover:text-white p-2">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-auto bg-slate-900">
            <iframe
              title="Dashboard Present"
              srcDoc={htmlContent}
              style={{ width: '100%', minHeight: '100%', border: 'none' }}
              sandbox="allow-scripts allow-same-origin"
            />
          </div>
        </div>
      )}

      {/* Share modal */}
      <ShareContentModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        contentType="dashboard"
        title={title || 'Dashboard'}
        userId={userId}
        htmlContent={htmlContent}
      />
    </div>
  );
});

export default DashboardWorkspace;
